import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOffIcon from '@mui/icons-material/EditOff';
import ReplayIcon from '@mui/icons-material/Replay';
import ShareIcon from '@mui/icons-material/Share';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DeleteIcon, EditIcon, RefreshIcon } from 'assets/icons';
import DebtStatusBadge from 'components/UI/DebtStatusBadge';
import FeedbackSnackbar from 'components/UI/FeedbackSnackbar';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDeleteReportMutation,
  useGetReportQuery,
  useRetryReportMutation,
  useShareReportMutation,
} from 'services/api/reports.api';
import type { ReportPeriodType, ReportStatus } from 'services/types/report';
import { NAVY, RADIUS_CARD, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import ChartSection from './components/ChartSection';
import ConversationSection from './components/ConversationSection';
import TextSection from './components/TextSection';

const formatDateLong = (date: Date): string => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  const dayName = days[date.getUTCDay()];
  const day = date.getUTCDate().toString().padStart(2, '0');
  const monthName = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${dayName}, ${day} de ${monthName} de ${year}`;
};

const formatDateTimeLocal = (dateString: string): string => {
  const date = new Date(dateString);
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

const getStatusBadge = (status: ReportStatus) => {
  const statusConfig: Record<ReportStatus, { text: string; color: 'success' | 'warning' | 'info' | 'error' }> = {
    COMPLETED: { text: 'Completado', color: 'success' },
    PENDING: { text: 'Pendiente', color: 'warning' },
    GENERATING: { text: 'Generando', color: 'info' },
    FAILED: { text: 'Error', color: 'error' },
  };
  const { text, color } = statusConfig[status];
  return <DebtStatusBadge text={text} color={color} />;
};

const getPeriodTypeBadge = (periodType: ReportPeriodType) => {
  const periodConfig: Record<ReportPeriodType, { text: string; color: 'primary' | 'info' | 'secondary' }> = {
    daily: { text: 'Diario', color: 'secondary' },
    weekly: { text: 'Semanal', color: 'primary' },
    monthly: { text: 'Mensual', color: 'info' },
  };
  const { text, color } = periodConfig[periodType];
  return <DebtStatusBadge text={text} color={color} />;
};

const iconBtnSx = {
  color: alpha(NAVY, 0.55),
  transition: 'all 0.15s ease-in-out',
  '&:hover': { bgcolor: alpha(NAVY, 0.06) },
} as const;

const ReportDetail: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.isAdmin ?? false;

  const { businessUnits } = useBusinessUnits();
  const { data: report, isLoading, refetch, isFetching } = useGetReportQuery(Number(reportId));
  const [retryReport, { isLoading: isRetrying }] = useRetryReportMutation();
  const [deleteReport, { isLoading: isDeleting }] = useDeleteReportMutation();
  const [shareReport, { isLoading: isSharing }] = useShareReportMutation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  const getBusinessUnitName = (businessUnitId: number) => {
    const bu = businessUnits?.find((b) => b.id === businessUnitId);
    return bu?.name || `BU #${businessUnitId}`;
  };

  const handleRetry = () => {
    if (!report) return;
    retryReport(report.id).unwrap().catch(console.error);
  };

  const handleDelete = () => {
    if (!report) return;
    if (!window.confirm('¿Estás seguro de que quieres eliminar este reporte?')) return;
    deleteReport(report.id)
      .unwrap()
      .then(() => navigate('/reports'))
      .catch(console.error);
  };

  const handleShare = () => {
    if (!report) return;
    shareReport(report.id)
      .unwrap()
      .then((response) => {
        navigator.clipboard.writeText(response.shareUrl).catch(console.error);
        setSnackbarMessage('Link copiado al portapapeles');
      })
      .catch(console.error);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={40} sx={{ color: alpha(NAVY, 0.3) }} />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ color: 'error.main', fontWeight: 600 }}>
          Reporte no encontrado
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reports')}
          sx={{ mt: 2, color: NAVY, borderRadius: RADIUS_COIN }}
        >
          Volver a la lista
        </Button>
      </Box>
    );
  }

  const sortedSections = [...(report.sections || [])].sort((a, b) => a.order - b.order);
  const visibleSections = isEditMode ? sortedSections : sortedSections.filter((s) => !s.isHidden);

  return (
    <Box sx={{ p: { xs: 2, lg: 3 }, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <IconButton
              size="small"
              sx={iconBtnSx}
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/reports');
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
              {report.title}
            </Typography>
            {getPeriodTypeBadge(report.periodType)}
            {getStatusBadge(report.status)}
          </Box>
          <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6), ml: 5.5 }}>
            {getBusinessUnitName(report.businessUnitId)} • {formatDateLong(new Date(report.beginDate))}
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: alpha(NAVY, 0.45), mt: 0.5, ml: 5.5 }}>
            Creado: {formatDateTimeLocal(report.createdAt)}
            {report.updatedAt !== report.createdAt && ` • Actualizado: ${formatDateTimeLocal(report.updatedAt)}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {report.status === 'COMPLETED' && (
            <Tooltip title="Compartir reporte">
              <IconButton onClick={handleShare} disabled={isSharing} sx={iconBtnSx}>
                {isSharing ? <CircularProgress size={20} sx={{ color: alpha(NAVY, 0.3) }} /> : <ShareIcon />}
              </IconButton>
            </Tooltip>
          )}
          {isAdmin && (
            <Tooltip title={isEditMode ? 'Salir de modo edición' : 'Modo edición'}>
              <IconButton
                onClick={() => setIsEditMode(!isEditMode)}
                sx={{ ...iconBtnSx, ...(isEditMode ? { color: NAVY, bgcolor: alpha(NAVY, 0.08) } : {}) }}
              >
                {isEditMode ? <EditOffIcon /> : <EditIcon />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={() => {
              refetch().catch(console.error);
            }}
            disabled={isFetching}
            sx={iconBtnSx}
          >
            <RefreshIcon />
          </IconButton>
          {report.status === 'FAILED' && (
            <Button
              variant="outlined"
              startIcon={isRetrying ? <CircularProgress size={16} /> : <ReplayIcon />}
              onClick={handleRetry}
              disabled={isRetrying}
              sx={{
                borderRadius: RADIUS_COIN,
                borderColor: alpha(NAVY, 0.15),
                color: 'warning.main',
                '&:hover': { borderColor: 'warning.main', bgcolor: alpha('#CF5C09', 0.06) },
              }}
            >
              Reintentar
            </Button>
          )}
          {isAdmin && (
            <IconButton
              onClick={handleDelete}
              disabled={isDeleting}
              sx={{ ...iconBtnSx, '&:hover': { bgcolor: 'error.light', color: 'error.dark' } }}
            >
              {isDeleting ? <CircularProgress size={20} sx={{ color: alpha(NAVY, 0.3) }} /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Pending / Generating state */}
      {(report.status === 'PENDING' || report.status === 'GENERATING') && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2, color: alpha(NAVY, 0.3) }} />
          <Typography variant="h6" sx={{ color: alpha(NAVY, 0.6), fontWeight: 500 }}>
            {report.status === 'PENDING' ? 'Preparando reporte...' : 'Generando reporte...'}
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(NAVY, 0.45) }}>
            Esto puede tomar unos minutos. Usa el botón de recargar para obtener las actualizaciones
          </Typography>
        </Box>
      )}

      {/* Failed state */}
      {report.status === 'FAILED' && sortedSections.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            bgcolor: 'error.light',
            borderRadius: RADIUS_CARD,
          }}
        >
          <Typography variant="h6" sx={{ color: 'error.dark', fontWeight: 600 }}>
            Error al generar el reporte
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.dark', mb: 2 }}>
            Hubo un problema al generar este reporte
          </Typography>
          <Button
            variant="contained"
            startIcon={<ReplayIcon />}
            onClick={handleRetry}
            disabled={isRetrying}
            sx={{ borderRadius: RADIUS_COIN, bgcolor: 'error.main' }}
          >
            Reintentar
          </Button>
        </Box>
      )}

      {/* Sections grid */}
      {visibleSections.length > 0 && (
        <Grid container spacing={2}>
          {visibleSections.map((section) => (
            <Grid size={{ xs: 12, md: section.width }} key={section.id}>
              {section.sectionType === 'chart' && (
                <ChartSection section={section} reportId={report.id} isEditMode={isEditMode} />
              )}
              {section.sectionType === 'conversation' && (
                <ConversationSection section={section} reportId={report.id} isEditMode={isEditMode} />
              )}
              {section.sectionType === 'text' && (
                <TextSection section={section} reportId={report.id} isEditMode={isEditMode} />
              )}
            </Grid>
          ))}
        </Grid>
      )}

      <FeedbackSnackbar
        open={!!snackbarMessage}
        severity="success"
        message={snackbarMessage ?? ''}
        onClose={() => setSnackbarMessage(null)}
      />
    </Box>
  );
};

export default ReportDetail;
